'use client';

import { useState } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface ProgramCardProps {
  program: {
    id: string;
    title: string;
    description: string;
    image: string;
    alt: string;
    duration: string;
    level: string;
    skillsCount: number;
    projectsCount: number;
    studentsEnrolled: number;
    rating: number;
    category: string;
    price: string;
    featured: boolean;
    active: boolean;
    url: string;
  };
}

export default function ProgramCard({ program }: ProgramCardProps) {
  const [isHovered, setIsHovered] = useState(false);

  const levelColors = {
    Beginner: 'bg-success/10 text-success',
    Intermediate: 'bg-warning/10 text-warning',
    Advanced: 'bg-error/10 text-error',
  };

  return (
    <div
      className={`group relative bg-card rounded-xl shadow-sky-sm transition-all duration-300 overflow-hidden border border-border ${
        program.active
          ? 'hover:shadow-sky-md cursor-pointer'
          : 'opacity-60 cursor-not-allowed'
      }`}
      onMouseEnter={() => program.active && setIsHovered(true)}
      onMouseLeave={() => program.active && setIsHovered(false)}
    >
      {program.featured && (
        <div className="absolute top-4 right-4 z-10 px-3 py-1 bg-gradient-to-r from-primary to-accent text-primary-foreground text-xs font-headline font-semibold rounded-full shadow-sky-sm">
          Featured
        </div>
      )}

      <div className="relative h-48 overflow-hidden">
        <AppImage
          src={program.image}
          alt={program.alt}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      </div>

      <div className="p-6">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-body font-medium text-primary bg-primary/10 px-3 py-1 rounded-full">
            {program.category}
          </span>
          <span
            className={`text-xs font-body font-medium px-3 py-1 rounded-full ${
              levelColors[program.level as keyof typeof levelColors]
            }`}
          >
            {program.level}
          </span>
        </div>

        <h3 className="text-xl font-headline font-bold text-foreground mb-2 line-clamp-2 group-hover:text-primary transition-colors duration-300">
          {program.title}
        </h3>

        <p className="text-sm font-body text-muted-foreground mb-4 line-clamp-2">
          {program.description}
        </p>

        <div className="flex items-center gap-4 mb-4 pb-4 border-b border-border">
          <div className="flex items-center gap-1.5">
            <Icon name="ClockIcon" size={16} variant="outline" className="text-muted-foreground" />
            <span className="text-xs font-body text-muted-foreground">{program.duration}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Icon name="AcademicCapIcon" size={16} variant="outline" className="text-muted-foreground" />
            <span className="text-xs font-body text-muted-foreground">{program.skillsCount} Skills</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Icon name="CodeBracketIcon" size={16} variant="outline" className="text-muted-foreground" />
            <span className="text-xs font-body text-muted-foreground">{program.projectsCount} Projects</span>
          </div>
        </div>

        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1">
              <Icon name="StarIcon" size={16} variant="solid" className="text-warning" />
              <span className="text-sm font-headline font-semibold text-foreground">{program.rating}</span>
            </div>
            <span className="text-xs font-body text-muted-foreground">
              ({program.studentsEnrolled.toLocaleString()} students)
            </span>
          </div>
          <span className="text-lg font-headline font-bold text-primary">{program.price}</span>
        </div>

        <Link
          href={program.active ? program.url : '#'}
          className={`block w-full py-3 text-center rounded-lg font-headline font-semibold text-sm transition-all duration-300 ${
            program.active
              ? 'bg-gradient-to-r from-primary to-accent text-primary-foreground shadow-sky-sm hover:shadow-sky-md hover:scale-105 cursor-pointer'
              : 'bg-muted text-muted-foreground cursor-not-allowed'
          }`}
          onClick={(e) => {
            if (!program.active) {
              e.preventDefault();
            }
          }}
        >
          {program.active ? 'View Program' : 'Next Batch Soon'}
        </Link>
      </div>
    </div>
  );
}